import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Cookie, X } from 'lucide-react'

const STORAGE_KEY = 'ap_cookie_consent_v1'

type ConsentValue = 'granted' | 'denied'

interface ConsentState {
  analytics: ConsentValue
  ad: ConsentValue
  functionality: ConsentValue
  decidedAt: string
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
    clarity?: (...args: unknown[]) => void
  }
}

const applyConsent = (state: Pick<ConsentState, 'analytics' | 'ad' | 'functionality'>) => {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  const gtag = window.gtag || ((...args: unknown[]) => {
    window.dataLayer!.push(args)
  })
  window.gtag = gtag
  gtag('consent', 'update', {
    ad_storage: state.ad,
    ad_user_data: state.ad,
    ad_personalization: state.ad,
    analytics_storage: state.analytics,
    functionality_storage: state.functionality,
    personalization_storage: state.functionality,
    security_storage: 'granted',
  })
  // Microsoft Clarity (public/clarity-init.js): mesmo sinal que o GA4. Com
  // "denied" o Clarity segue gravando sessão sem cookies nem identificador
  // persistente; "granted" libera _clck/_clsk. O init já enfileira "denied"
  // por padrão, então aqui só reflete a decisão do usuário.
  window.clarity?.('consentv2', {
    ad_Storage: state.ad,
    analytics_Storage: state.analytics,
  })
}

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as ConsentState
        applyConsent(parsed)
        return
      } catch {
        // fall-through to show banner again
      }
    }
    setVisible(true)
  }, [])

  const persist = (state: Pick<ConsentState, 'analytics' | 'ad' | 'functionality'>) => {
    const full: ConsentState = { ...state, decidedAt: new Date().toISOString() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(full))
    applyConsent(full)
    setVisible(false)
  }

  const acceptAll = () => persist({ analytics: 'granted', ad: 'granted', functionality: 'granted' })
  const rejectAll = () => persist({ analytics: 'denied', ad: 'denied', functionality: 'denied' })
  const onlyEssential = rejectAll

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
      className="fixed bottom-4 left-4 right-4 z-[9999] sm:left-6 sm:right-auto sm:max-w-md"
    >
      <div className="bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 shadow-caderno-xl rounded-caderno-md p-5">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-terra-100 dark:bg-terra-500/30 shrink-0">
            <Cookie className="w-5 h-5 text-terra-500 dark:text-terra-300" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-cream-50">
              Sua privacidade importa
            </h2>
            <p className="text-sm text-ink-500 dark:text-ink-300 mt-1">
              Usamos cookies para autenticação, melhorar a experiência e medir o uso da
              plataforma. Você pode aceitar todos ou apenas os essenciais. Saiba mais na{' '}
              <Link to="/cookies" className="text-terra-500 dark:text-terra-300 underline">
                Política de Cookies
              </Link>{' '}
              e na{' '}
              <Link to="/privacy" className="text-terra-500 dark:text-terra-300 underline">
                Política de Privacidade
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={acceptAll}
                className="px-4 py-2 bg-terra-500 text-white rounded-caderno-sm text-sm hover:bg-terra-700 transition-colors"
              >
                Aceitar todos
              </button>
              <button
                onClick={onlyEssential}
                className="px-4 py-2 bg-cream-200 dark:bg-ink-700 text-ink-700 dark:text-cream-50 rounded-caderno-sm text-sm hover:bg-cream-200 dark:hover:bg-ink-700 transition-colors"
              >
                Apenas essenciais
              </button>
            </div>
          </div>
          <button
            onClick={onlyEssential}
            aria-label="Fechar e usar apenas cookies essenciais"
            className="p-1 text-ink-400 hover:text-ink-700 dark:hover:text-cream-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default CookieConsent
